import type { BaseEntity, EntityServiceClient } from "@brains/plugins";
import { basename, dirname, extname } from "path";
import { resolveInSyncPath, toSyncRelativePath } from "./path-utils";
import {
  getMimeTypeForExtension,
  IMAGE_EXTENSIONS,
  isImageFile,
} from "./image-file-utils";
import {
  DOCUMENT_SIDECAR_SUFFIX,
  getDocumentMimeTypeForExtension,
  getDocumentSidecarPath,
  isDocumentFile,
  isDocumentSidecarFile,
} from "./document-file-utils";
import {
  buildEntityFilePath,
  containmentOf,
  getEntityFileExtension,
  parseEntityPath,
  resolveEntityPlacement,
  type Containment,
} from "./entity-paths";
import { EntityPlacementError } from "./entity-placement-error";
import { mkdir, readFile, unlink, stat, utimes } from "fs/promises";
import { writeFileAtomic } from "./atomic-write";
import { z } from "@brains/utils/zod";
import { computeContentHash } from "@brains/utils/hash";
import type { RawEntity, DirectorySyncStatus } from "../types";
import {
  ensureDirectoryStructure as ensureSyncDirectoryStructure,
  gatherFileStatus as gatherSyncFileStatus,
  getAllMarkdownFiles as findMarkdownFiles,
  getAllSyncFiles as findSyncFiles,
} from "./file-discovery";
import { pathExists } from "./fs-utils";
import { OversizedFileError } from "./oversized-file-error";
import type { PendingDeleteTarget } from "./pending-delete-registry";

export { IMAGE_EXTENSIONS, isImageFile } from "./image-file-utils";
export { DOCUMENT_EXTENSIONS, isDocumentFile } from "./document-file-utils";

export type FileOperationsEntityService = Pick<
  EntityServiceClient,
  "serializeEntity" | "hasEntityType" | "getEntityTypes" | "getEntityTypeConfig"
>;

const sidecarMetadataSchema = z.record(z.string(), z.unknown());

/**
 * Handles file I/O operations for directory sync
 */
export class FileOperations {
  private readonly syncPath: string;
  private readonly entityService: FileOperationsEntityService;

  constructor(syncPath: string, entityService: FileOperationsEntityService) {
    this.syncPath = syncPath;
    this.entityService = entityService;
  }

  parseEntityFromPath(filePath: string): { entityType: string; id: string } {
    return parseEntityPath(this.syncPath, filePath, this.containment());
  }

  /** Where contained types live, as the registry holds it now. */
  private containment(): Containment {
    return containmentOf(this.entityService);
  }

  getPendingDeleteTarget(filePath: string): PendingDeleteTarget | undefined {
    const relativePath = toSyncRelativePath(this.syncPath, filePath);
    const entityPath = isDocumentSidecarFile(relativePath)
      ? relativePath.slice(0, -DOCUMENT_SIDECAR_SUFFIX.length)
      : relativePath;
    const { entityType, id } = this.parseEntityFromPath(entityPath);
    if (!this.entityService.hasEntityType(entityType)) return undefined;

    const isSyncFile =
      entityPath.endsWith(".md") ||
      (entityType === "image" && isImageFile(entityPath)) ||
      (entityType === "document" && isDocumentFile(entityPath));
    if (!isSyncFile) return undefined;

    return {
      entityType,
      entityId: id,
      filePath: resolveInSyncPath(this.syncPath, entityPath),
    };
  }

  async readEntity(filePath: string, maxBytes?: number): Promise<RawEntity> {
    const fullPath = resolveInSyncPath(this.syncPath, filePath);

    const stats = await stat(fullPath);
    if (maxBytes !== undefined && stats.size > maxBytes) {
      throw new OversizedFileError(filePath, stats.size, maxBytes);
    }

    const { entityType, id } = this.parseEntityFromPath(filePath);

    // Fallback to mtime if birthtime is invalid (zero epoch)
    const created =
      stats.birthtime.getTime() > 0 ? stats.birthtime : stats.mtime;
    const updated = stats.mtime;

    let content: string;
    let metadata: Record<string, unknown> | undefined;
    if (isImageFile(filePath) || isDocumentFile(filePath)) {
      const buffer = await readFile(fullPath);
      const base64 = buffer.toString("base64");
      const ext = extname(filePath);
      const mimeType = isDocumentFile(filePath)
        ? getDocumentMimeTypeForExtension(ext)
        : getMimeTypeForExtension(ext);
      content = `data:${mimeType};base64,${base64}`;
      if (isDocumentFile(filePath)) {
        metadata = await this.readDocumentSidecar(fullPath, filePath);
      }
    } else {
      content = await readFile(fullPath, "utf-8");
    }

    const result: RawEntity = {
      entityType,
      id,
      content,
      created,
      updated,
    };
    if (metadata) {
      result.metadata = metadata;
    }
    return result;
  }

  /**
   * Read the sidecar JSON for a document file. Returns metadata enriched with
   * a `filename` derived from the file path (acts as a default for documents
   * dropped in by hand without a sidecar) so the document schema's required
   * filename always has a value.
   */
  private async readDocumentSidecar(
    fullPdfPath: string,
    relativePath: string,
  ): Promise<Record<string, unknown>> {
    const defaults: Record<string, unknown> = {
      mimeType: "application/pdf",
      filename: basename(relativePath),
    };
    const sidecarPath = getDocumentSidecarPath(fullPdfPath);

    if (!(await pathExists(sidecarPath))) {
      return defaults;
    }

    try {
      const raw = await readFile(sidecarPath, "utf-8");
      const parsed = sidecarMetadataSchema.safeParse(JSON.parse(raw));
      return { ...defaults, ...(parsed.success ? parsed.data : {}) };
    } catch {
      // Corrupt sidecar shouldn't block import; the schema will still pass
      // because filename + mimeType come from defaults.
      return defaults;
    }
  }

  /** Shared pure admission for automatic/manual exports and orphan cleanup. */
  assertEntityPlacement(entity: BaseEntity): void {
    this.assertPlacement(
      entity.entityType,
      entity.id,
      getEntityFileExtension(entity),
    );
  }

  private assertPlacement(
    entityType: string,
    entityId: string,
    extension: string,
  ): void {
    const placement = resolveEntityPlacement(
      this.syncPath,
      entityType,
      entityId,
      extension,
      this.containment(),
    );
    if (!placement.writable) {
      throw new EntityPlacementError(
        entityType,
        entityId,
        placement.relativePath,
        placement.owner,
      );
    }
  }

  /**
   * Write entity to file
   * Skips write if serialized content matches current file content
   */
  async writeEntity(entity: BaseEntity): Promise<void> {
    this.assertEntityPlacement(entity);
    const filePath = this.getEntityFilePath(entity);
    const isImage = entity.entityType === "image";
    const isDocument = entity.entityType === "document";

    // The durable outbox keeps only the latest mutation per (type, id).
    // Image extensions are content-derived, so converge that stable namespace
    // by removing every obsolete representation before writing the latest one.
    if (isImage) {
      await Promise.all(
        this.getEntityDeletePaths(entity.entityType, entity.id)
          .filter((candidate) => candidate !== filePath)
          .map(async (candidate) => {
            try {
              await unlink(candidate);
            } catch (error) {
              if (
                typeof error === "object" &&
                error !== null &&
                "code" in error &&
                error.code === "ENOENT"
              ) {
                return;
              }
              throw error;
            }
          }),
      );
    }

    if (isImage || isDocument) {
      const dataUrlPattern = isImage
        ? /^data:image\/[a-z+]+;base64,(.+)$/i
        : /^data:application\/pdf;base64,(.+)$/i;
      const match = entity.content.match(dataUrlPattern);
      const contentToWrite = match?.[1]
        ? Buffer.from(match[1], "base64")
        : Buffer.from(entity.content, "base64");

      let binaryUnchanged = false;
      if (await pathExists(filePath)) {
        const currentContent = await readFile(filePath);
        const currentHash = computeContentHash(
          currentContent.toString("base64"),
        );
        const newHash = computeContentHash(contentToWrite.toString("base64"));

        if (currentHash === newHash) {
          binaryUnchanged = true;
        }
      }

      if (!binaryUnchanged) {
        await this.ensureEntityDirectory(filePath);
        await writeFileAtomic(filePath, contentToWrite);
      }

      if (isDocument) {
        await this.writeDocumentSidecar(entity, filePath);
      }

      if (binaryUnchanged) {
        return;
      }
    } else {
      const contentToWrite = this.entityService.serializeEntity(entity);

      if (await pathExists(filePath)) {
        const currentContent = await readFile(filePath, "utf-8");
        const currentHash = computeContentHash(currentContent);
        const newHash = computeContentHash(contentToWrite);

        if (currentHash === newHash) {
          return;
        }
      }

      await this.ensureEntityDirectory(filePath);
      await writeFileAtomic(filePath, contentToWrite);
    }

    // Preserve entity timestamps on the file to prevent unnecessary re-syncs
    const updatedTime = new Date(entity.updated);
    await utimes(filePath, updatedTime, updatedTime);
  }

  private async ensureEntityDirectory(filePath: string): Promise<void> {
    await mkdir(dirname(filePath), { recursive: true });
  }

  /**
   * Persist document metadata that does not survive in the PDF bytes
   * (filename, page count, dedup key, source provenance) in a sidecar JSON
   * file. `mimeType` is omitted because it is implicit in the .pdf extension
   * and would be regenerated from the data URL on read.
   */
  private async writeDocumentSidecar(
    entity: BaseEntity,
    pdfPath: string,
  ): Promise<void> {
    const metadata = entity.metadata;
    const persistable: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(metadata)) {
      if (key === "mimeType") continue;
      if (value === undefined) continue;
      persistable[key] = value;
    }

    const sidecarPath = getDocumentSidecarPath(pdfPath);
    const serialized = `${JSON.stringify(persistable, null, 2)}\n`;

    if (await pathExists(sidecarPath)) {
      const existing = await readFile(sidecarPath, "utf-8");
      if (existing === serialized) {
        return;
      }
    }

    await this.ensureEntityDirectory(sidecarPath);
    await writeFileAtomic(sidecarPath, serialized);
  }

  getFilePath(
    entityId: string,
    entityType: string,
    extension: string = ".md",
  ): string {
    return buildEntityFilePath(
      this.syncPath,
      entityId,
      entityType,
      extension,
      this.containment(),
    );
  }

  getEntityFilePath(entity: BaseEntity): string {
    return this.getFilePath(
      entity.id,
      entity.entityType,
      getEntityFileExtension(entity),
    );
  }

  getEntityWritePaths(entity: BaseEntity): string[] {
    const filePath = this.getEntityFilePath(entity);
    return entity.entityType === "document"
      ? [filePath, getDocumentSidecarPath(filePath)]
      : [filePath];
  }

  /** Every path that may change while converging the latest entity revision. */
  getEntityConvergencePaths(entity: BaseEntity): string[] {
    return entity.entityType === "image"
      ? this.getEntityDeletePaths(entity.entityType, entity.id)
      : this.getEntityWritePaths(entity);
  }

  getEntityDeletePaths(entityType: string, entityId: string): string[] {
    if (entityType === "document") {
      const filePath = this.getFilePath(entityId, entityType, ".pdf");
      return [filePath, getDocumentSidecarPath(filePath)];
    }
    const extensions =
      entityType === "image" ? [...IMAGE_EXTENSIONS, ".md"] : [".md"];
    return extensions.map((extension) =>
      this.getFilePath(entityId, entityType, extension),
    );
  }

  async deleteEntityFiles(entityType: string, entityId: string): Promise<void> {
    this.assertPlacement(
      entityType,
      entityId,
      entityType === "document"
        ? ".pdf"
        : entityType === "image"
          ? (IMAGE_EXTENSIONS[0] ?? ".md")
          : ".md",
    );
    await Promise.all(
      this.getEntityDeletePaths(entityType, entityId).map(async (filePath) => {
        try {
          await unlink(filePath);
        } catch (error) {
          if (
            typeof error === "object" &&
            error !== null &&
            "code" in error &&
            error.code === "ENOENT"
          ) {
            return;
          }
          throw error;
        }
      }),
    );
  }

  async getAllMarkdownFiles(): Promise<string[]> {
    return findMarkdownFiles(this.syncPath, this.entityService);
  }

  /**
   * Get all syncable files in sync directory (markdown + binary media files)
   */
  async getAllSyncFiles(): Promise<string[]> {
    return findSyncFiles(this.syncPath, this.entityService);
  }

  /**
   * Ensure directory structure exists
   */
  async ensureDirectoryStructure(entityTypes: string[]): Promise<void> {
    // A contained type lives in its container's folder, not one of its own.
    const containment = this.containment();
    await ensureSyncDirectoryStructure(
      this.syncPath,
      entityTypes.filter((type) => !containment.has(type)),
    );
  }

  /**
   * Check if entity should be updated based on content hash
   * Uses stored contentHash from existing entity for efficiency
   */
  shouldUpdateEntity(existing: BaseEntity, newEntity: RawEntity): boolean {
    const newHash = computeContentHash(newEntity.content);
    return existing.contentHash !== newHash;
  }

  /**
   * Gather file status information for directory sync status
   */
  async gatherFileStatus(): Promise<{
    files: DirectorySyncStatus["files"];
    stats: DirectorySyncStatus["stats"];
  }> {
    return gatherSyncFileStatus(
      this.syncPath,
      this.entityService,
      this.containment(),
    );
  }

  async syncDirectoryExists(): Promise<boolean> {
    return pathExists(this.syncPath);
  }

  async fileExists(filePath: string): Promise<boolean> {
    return pathExists(filePath);
  }
}
